import _ from 'lodash';
import { useMemo, useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import {
  selectAiChannel,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  useReportStore,
} from '@/entities/report';
import { getSelectedEvent, groupSelectedColumnInfo } from './helper';

/**
 * Supplies the HR summary tab with the currently selected ECG event and the column of events it belongs to. Reads selectedColumnInfo, aiChannel and selectedEventIndex from the report store under the TAB_6 key, memoizes the resolved event and a grouped copy of the column, and resets a per-group index ref so switching tabs can return to the previously viewed event.
 *
 * @returns An object carrying selectedColumnInfo, aiChannel, selectedEventIndex, setSelectedEventIndex, selectedEvent, memoIndexes and formatSelectedColumnInfo.
 */
const useSelectedInfo = () => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const setSelectedEventIndexAction = useReportStore((s) => s.actions.setSelectedEventIndex);
  /**
   * Moves the HR summary selection to another event in the currently selected column. Forwards the new index, or an updater for it, to the report store action bound to the TAB_6 key.
   *
   * @param v - The new selected event index, or an updater receiving the previous index.
   * @returns Nothing.
   */
  const setSelectedEventIndex = (v: Parameters<typeof setSelectedEventIndexAction>[1]) =>
    setSelectedEventIndexAction(keyRecoil, v);

  const selectedEvent: any = useMemo(
    () => getSelectedEvent(selectedColumnInfo, selectedEventIndex),
    [selectedColumnInfo, selectedEventIndex],
  );

  //* Dùng để khi chuyển tab thì quay về event trong tab trước đó
  const memoIndexes = useRef<Record<string, any>>({});

  const formatSelectedColumnInfo = useMemo(() => {
    const formattedColumnInfo = groupSelectedColumnInfo(selectedColumnInfo);
    memoIndexes.current = {};
    _.forEach(formattedColumnInfo, (value, key) => {
      memoIndexes.current[key] = -1;
    });
    return formattedColumnInfo;
  }, [selectedColumnInfo]);

  return {
    selectedColumnInfo,
    aiChannel,
    selectedEventIndex,
    setSelectedEventIndex,
    selectedEvent,
    memoIndexes,
    formatSelectedColumnInfo,
  };
};

export default useSelectedInfo;
